import React from "react";
import { Link } from "react-router-dom";
import {
  Button,
  Checkbox,
  FormLayout,
  TextField,
  Text,
} from "@shopify/polaris";
import AuthContainer from "../../AuthContainer";
import BannerCustom from "components/Banners/BannerCustom";
import PageHelmet from "components/PageHelmet";
import passwordIMG from "img/icons/entities/password.svg";
import emailIMG from "img/icons/entities/email.svg";
import eyeOnIMG from "img/icons/misc/eye-on.svg";
import eyeOffIMG from "img/icons/misc/eye-off.svg";
import LoginButtons from "./LoginButtons";
import useLogInPage from "../../shared/hooks/useLogInPage";

export default function LogInPage() {
  const {
    t,
    navigate,
    loggingIn,
    step,
    passwordChecked,
    email,
    errorEmail,
    password,
    errorPassword,
    showPassword,
    twoFaCode,
    twoFaError,
    isDevOrLocalhost,
    setPasswordChecked,
    setEmail,
    validateEmail,
    togglePasswordVisibility,
    handlePasswordChange,
    doLogin,
    submitTwoFactor,
    handleBackToCredentials,
    handleTwoFaCodeChange,
    setErrorPassword,
    setTwoFaError,
  } = useLogInPage();

  const signUpFooter = (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "flex-end",
      }}
    >
      <h3 style={{ marginRight: 10 }}>{t("auth.login.dontHaveAccount")}</h3>
      <div style={{ width: "100px" }}>
        <Button
          fullWidth
          variant="secondary"
          onClick={() => navigate("/signup")}
        >
          {t("auth.login.signUp")}
        </Button>
      </div>
    </div>
  );

  if (step === "twoFactor") {
    return (
      <AuthContainer
        title={t("auth.twoFa.title")}
        onParrotClick={
          isDevOrLocalhost ? () => navigate("/manager") : undefined
        }
        content={
          <form
            autoComplete="off"
            onSubmit={(e) => {
              e.preventDefault();
              submitTwoFactor();
            }}
          >
            <PageHelmet title={t("auth.twoFa.pageTitle")} />
            {twoFaError ? (
              <BannerCustom
                status="critical"
                message={twoFaError}
                onDismiss={() => setTwoFaError("")}
              />
            ) : null}
            <FormLayout>
              <TextField
                name="code"
                type="text"
                label={
                  <Text color="subdued" variant="bodySm">
                    {t("auth.twoFa.codeLabel")}
                  </Text>
                }
                placeholder={t("auth.twoFa.codePlaceholder")}
                autoComplete="one-time-code"
                inputMode="numeric"
                value={twoFaCode}
                maxLength={6}
                onChange={handleTwoFaCodeChange}
              />
            </FormLayout>
          </form>
        }
        secondaryContent={signUpFooter}
        actions={
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: "95px",
              }}
            >
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleBackToCredentials();
                }}
                style={{
                  color: "#267FD7",
                  textDecoration: "none",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
                onMouseEnter={(e) => {
                  e.target.style.textDecoration = "underline";
                }}
                onMouseLeave={(e) => {
                  e.target.style.textDecoration = "none";
                }}
              >
                {t("auth.twoFa.backToSignIn")}
              </a>
              <Button
                disabled={loggingIn || twoFaCode.length !== 6}
                loading={loggingIn}
                variant="primary"
                onClick={submitTwoFactor}
              >
                {t("auth.twoFa.continue")}
              </Button>
            </div>
            <div style={{ height: "140px" }} />
          </>
        }
      />
    );
  }

  return (
    <AuthContainer
      title={t("auth.login.welcomeBack")}
      onParrotClick={
        isDevOrLocalhost ? () => navigate("/manager") : undefined
      }
      content={
        <form autoComplete="off">
          <PageHelmet title={t("auth.login.pageTitle")} />
          {errorPassword ? (
            <BannerCustom
              status="critical"
              message={errorPassword}
              onDismiss={() => setErrorPassword(false)}
            />
          ) : null}
          <FormLayout>
            <TextField
              name="email"
              type="email"
              label={
                <Text color="subdued" variant="bodySm">
                  {t("login.email")}
                </Text>
              }
              placeholder={t("login.email")}
              autoComplete="email"
              value={email}
              prefix={
                <img src={emailIMG} alt="email" style={{ marginTop: 6 }} />
              }
              error={errorEmail}
              onBlur={() => validateEmail(email)}
              onChange={(value) => {
                setEmail(value);
                setErrorPassword(false);
              }}
            />
            <TextField
              name="password"
              type={showPassword ? "text" : "password"}
              label={
                <Text color="subdued" variant="bodySm">
                  {t("login.password")}
                </Text>
              }
              placeholder={t("login.password")}
              value={password}
              prefix={
                <img
                  src={passwordIMG}
                  alt="password"
                  style={{ marginTop: 6 }}
                />
              }
              suffix={
                <img
                  src={showPassword ? eyeOffIMG : eyeOnIMG}
                  alt={
                    showPassword
                      ? t("auth.login.hidePassword")
                      : t("auth.login.showPassword")
                  }
                  style={{
                    marginTop: 6,
                    cursor: "pointer",
                    width: "20px",
                    height: "20px",
                  }}
                  onClick={togglePasswordVisibility}
                />
              }
              onChange={handlePasswordChange}
            />
            <Link className="Polaris-Link" to="/forgot">
              {t("login.forgotPass")}
            </Link>
          </FormLayout>
        </form>
      }
      secondaryContent={signUpFooter}
      actions={
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Checkbox
              checked={passwordChecked}
              label={t("login.rememberMe")}
              onChange={setPasswordChecked}
            />
            <Button
              disabled={loggingIn}
              loading={loggingIn}
              variant="primary"
              onClick={doLogin}
            >
              {t("login.signIn")}
            </Button>
          </div>
          <LoginButtons />
        </>
      }
    />
  );
}
